import { FalhaDaApi, lerProblema, segundosDoRetryAfter } from "./problema";
import type { SessaoAberta } from "./contrato";

/*
 * O cliente da API administrativa.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ MESMA ORIGEM, SEMPRE.                                                    │
 * │                                                                          │
 * │ Nenhuma URL absoluta aqui, e nenhuma variável de ambiente com endereço   │
 * │ de API. O painel é servido pelo MESMO hostname da API, com o Caddy       │
 * │ roteando `/admin/v1` e `/v1` para a API e o resto para cá.               │
 * │                                                                          │
 * │ Isso não é detalhe de roteamento: com mesma origem o cookie de sessão    │
 * │ pode ser `SameSite=Strict`. Com hostnames distintos ele teria de ser     │
 * │ `SameSite=None`, o que abre CSRF num painel que cancela boleto e emite   │
 * │ chave de API. A razão está escrita dentro do manifesto do Deployment.    │
 * └──────────────────────────────────────────────────────────────────────────┘
 */
const PREFIXO = "/admin/v1";

/** Rotas de sessão, que o cookie `bm_refresh` alcança pelo `Path`. */
export const ROTA_SESSAO = `${PREFIXO}/sessao`;
export const ROTA_RENOVACAO = `${PREFIXO}/sessao/renovacao`;

export interface PedidoDeLogin {
  email: string;
  senha: string;
}

interface Opcoes {
  metodo?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  corpo?: unknown;
  /** `multipart/form-data`. O `Content-Type` é do navegador, que escolhe a fronteira. */
  formulario?: FormData;
  busca?: Record<string, string | number | undefined>;
  sinal?: AbortSignal;
  /** O `Accept` do pedido. Só a exportação pede outra coisa que JSON. */
  aceita?: string;
}

/** Um arquivo gerado pelo servidor, como a exportação CSV de faturas. */
export interface ArquivoBaixado {
  conteudo: Blob;
  /** O nome que o servidor deu em `Content-Disposition`, ou o de reserva. */
  nome: string;
  /** Cabeçalhos de resposta, para o que a rota anexa (`X-Exportacao-Truncada`). */
  cabecalhos: Headers;
}

export interface DependenciasDoCliente {
  /** O access token vivo, ou `null` quando não há sessão. Vem da memória. */
  obterToken: () => string | null;
  /** Chamado quando uma renovação silenciosa deu certo. */
  aoRenovar: (sessao: SessaoAberta) => void;
  /** Chamado quando a sessão acabou e só resta entrar de novo. */
  aoPerderSessao: () => void;
}

export class ClienteDaApi {
  readonly #dep: DependenciasDoCliente;

  /*
   * A renovação em voo, compartilhada.
   *
   * O refresh é ROTATIVO: cada uso invalida o anterior, e reapresentar um já
   * consumido revoga TODAS as sessões do operador. Com a visão geral em polling
   * de 10 s e mais duas listas na tela, o access expirando dispara várias
   * respostas 401 no mesmo instante — e cada uma tentaria renovar. A segunda
   * chegaria com o refresh que a primeira acabou de gastar, e o operador seria
   * desconectado por um mecanismo de defesa funcionando exatamente como deve.
   *
   * Uma promessa só, compartilhada por todas as chamadas que esperam.
   */
  #renovacaoEmVoo: Promise<boolean> | null = null;

  constructor(dependencias: DependenciasDoCliente) {
    this.#dep = dependencias;
  }

  /**
   * Abre a sessão com e-mail e senha.
   *
   * SEM segundo fator, desde 2026-09-08: o doc 06 §2.1 exigia TOTP, e a change
   * 20c executou a segunda saída do doc 06b §5, com o risco aceito por escrito
   * (D-256). A senha é a credencial completa.
   */
  async entrar(pedido: PedidoDeLogin): Promise<SessaoAberta> {
    return this.#json<SessaoAberta>(
      await this.#enviar(ROTA_SESSAO, { metodo: "POST", corpo: pedido }, false),
    );
  }

  /**
   * Encerra a sessão no servidor.
   *
   * Falha aqui NÃO impede o painel de esquecer a sessão localmente: se o
   * servidor não respondeu, insistir só mantém na tela um operador que pediu
   * para sair. Quem decide isso é quem chama.
   */
  async sair(): Promise<void> {
    await this.#enviar(ROTA_SESSAO, { metodo: "DELETE" }, true);
  }

  async obter<T>(caminho: string, opcoes: Omit<Opcoes, "metodo" | "corpo"> = {}): Promise<T> {
    return this.#json<T>(await this.#comRenovacao(caminho, { ...opcoes, metodo: "GET" }));
  }

  async enviar<T>(caminho: string, opcoes: Opcoes = {}): Promise<T> {
    return this.#json<T>(
      await this.#comRenovacao(caminho, { metodo: "POST", ...opcoes }),
    );
  }

  /**
   * Envia um formulário, com a mesma sessão e a mesma renovação.
   *
   * O segredo do formulário vai só no corpo: nem na URL, nem em cabeçalho, nem em
   * log. Depois do `401`, a segunda tentativa reaproveita o mesmo `FormData`, que é
   * o que dispensa pedir o arquivo de novo a quem teve a sessão renovada no meio.
   */
  async enviarFormulario<T>(
    caminho: string,
    formulario: FormData,
    metodo: "POST" | "PUT" = "PUT",
  ): Promise<T> {
    return this.#json<T>(await this.#comRenovacao(caminho, { metodo, formulario }));
  }

  /**
   * Baixa um arquivo gerado no servidor, com a mesma sessão e a mesma renovação
   * das leituras.
   *
   * Por `fetch`, e não por um `<a href>` direto para a rota: o access token
   * vive em memória e vai no `Authorization`, que um link do navegador não
   * envia. Um link funcionaria só com cookie de acesso — e o access token não
   * vai para cookie, de propósito (ver `sessao/loja.ts`).
   */
  async baixar(
    caminho: string,
    nomeDeReserva: string,
    opcoes: Omit<Opcoes, "metodo" | "corpo"> = {},
  ): Promise<ArquivoBaixado> {
    const resposta = await this.#comRenovacao(caminho, { ...opcoes, metodo: "GET" });
    if (!resposta.ok) {
      throw new FalhaDaApi(
        resposta.status,
        await lerProblema(resposta),
        `${resposta.status} em ${resposta.url}`,
        segundosDoRetryAfter(resposta.headers.get("retry-after")),
      );
    }

    return {
      conteudo: await resposta.blob(),
      nome: nomeDoAnexo(resposta.headers.get("content-disposition")) ?? nomeDeReserva,
      cabecalhos: resposta.headers,
    };
  }

  /*
   * Uma tentativa, uma renovação, uma segunda tentativa. Nunca mais que isso:
   * um laço de renovação diante de um 401 permanente queimaria o balde de taxa
   * do login e derrubaria a sessão de quem está do outro lado.
   */
  async #comRenovacao(caminho: string, opcoes: Opcoes): Promise<Response> {
    const primeira = await this.#enviar(caminho, opcoes, true);
    if (primeira.status !== 401) return primeira;

    if (!(await this.#renovar())) {
      this.#dep.aoPerderSessao();
      return primeira;
    }

    return this.#enviar(caminho, opcoes, true);
  }

  async #renovar(): Promise<boolean> {
    this.#renovacaoEmVoo ??= this.#renovarUmaVez().finally(() => {
      this.#renovacaoEmVoo = null;
    });
    return this.#renovacaoEmVoo;
  }

  async #renovarUmaVez(): Promise<boolean> {
    try {
      /*
       * SEM CORPO. O refresh é lido do cookie `bm_refresh`, que o navegador
       * anexa sozinho por causa do `Path=/admin/v1/sessao`. Um token que o
       * JavaScript precisasse enviar seria um token que o JavaScript pode ler —
       * e o `HttpOnly` existe justamente para que ele não possa.
       */
      const resposta = await this.#enviar(ROTA_RENOVACAO, { metodo: "POST" }, false);
      if (!resposta.ok) return false;

      this.#dep.aoRenovar((await resposta.json()) as SessaoAberta);
      return true;
    } catch {
      // Falha de transporte não é sessão perdida. Quem chamou trata o erro
      // original; encerrar a sessão aqui poria o operador de volta no login por
      // causa de uma oscilação de rede.
      return false;
    }
  }

  async #enviar(caminho: string, opcoes: Opcoes, comToken: boolean): Promise<Response> {
    const cabecalhos = new Headers({ Accept: opcoes.aceita ?? "application/json" });

    if (comToken) {
      const token = this.#dep.obterToken();
      if (token) cabecalhos.set("Authorization", `Bearer ${token}`);
    }

    let corpo: string | FormData | undefined = opcoes.formulario;
    if (opcoes.corpo !== undefined) {
      cabecalhos.set("Content-Type", "application/json");
      corpo = JSON.stringify(opcoes.corpo);
    }

    const url = caminho.startsWith("/") ? caminho : `${PREFIXO}/${caminho}`;

    return fetch(comBusca(url, opcoes.busca), {
      method: opcoes.metodo ?? "GET",
      headers: cabecalhos,
      // Espalhado condicionalmente por causa de `exactOptionalPropertyTypes`:
      // com ele ligado, `body: undefined` não é o mesmo que ausência de corpo, e
      // o compilador está certo em recusar.
      ...(corpo !== undefined ? { body: corpo } : {}),
      /*
       * `same-origin` é o padrão do fetch, e está escrito assim mesmo — o
       * cookie de refresh depende dele, e um default implícito é o tipo de
       * coisa que alguém "limpa" numa refatoração sem saber o que quebrou.
       */
      credentials: "same-origin",
      /*
       * A API já responde `Cache-Control: private, no-store` em toda rota
       * administrativa. Repetir a intenção aqui protege do caso em que um
       * intermediário reescreve o cabeçalho: a leitura atravessa os tenants, e
       * a URL não carrega o tenant.
       */
      cache: "no-store",
      ...(opcoes.sinal ? { signal: opcoes.sinal } : {}),
    });
  }

  async #json<T>(resposta: Response): Promise<T> {
    if (!resposta.ok) {
      throw new FalhaDaApi(
        resposta.status,
        await lerProblema(resposta),
        `${resposta.status} em ${resposta.url}`,
        segundosDoRetryAfter(resposta.headers.get("retry-after")),
      );
    }

    // `204` e `202` sem corpo são respostas legítimas de várias ações.
    if (resposta.status === 204 || resposta.headers.get("content-length") === "0") {
      return undefined as T;
    }

    return (await resposta.json()) as T;
  }
}

/**
 * O `filename` de `Content-Disposition: attachment; filename="faturas-2026-10.csv"`.
 *
 * Só o nome simples, sem barra nem caminho: o valor vem da rede, e o navegador
 * já recusa caminho no atributo `download` — conferir aqui deixa a regra
 * escrita em vez de implícita.
 */
function nomeDoAnexo(disposicao: string | null): string | null {
  const nome = /filename="?([^";]+)"?/i.exec(disposicao ?? "")?.[1]?.trim();
  if (!nome || /[\\/]/.test(nome)) return null;
  return nome;
}

function comBusca(url: string, busca: Opcoes["busca"]): string {
  if (!busca) return url;

  const parametros = new URLSearchParams();
  for (const [nome, valor] of Object.entries(busca)) {
    // `undefined` é "filtro não aplicado", e não string vazia: mandar `banco=`
    // faria a API filtrar por banco nenhum em vez de não filtrar.
    if (valor !== undefined && valor !== "") parametros.set(nome, String(valor));
  }

  const consulta = parametros.toString();
  return consulta ? `${url}?${consulta}` : url;
}
